import { Banner } from "@/components/flexkit/Banner";
import { Button } from "@/components/flexkit/Button";
import { Link } from "@/components/flexkit/Link";
import { NotAuthorized } from "@/components/NotAuthorized";
import { LoadingState } from "@/components/podkit/loading/LoadingState";
import { Textarea } from "@/components/podkit/forms/Textarea";
import { Text } from "@/components/podkit/typography/Text";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { Switch } from "@/components/Switch";
import { useDocumentTitle } from "@/hooks/use-document-title";
import { useMembership } from "@/hooks/use-membership";
import { useGetAnnouncementBanner, useOrganization, useUpdateAnnouncementBanner } from "@/queries/organization-queries";
import { formatError } from "@/utils/errors";
import { AnnouncementMarkdown } from "@/components/AnnouncementMarkdown";
import { OrganizationRole, OrganizationTier } from "gitpod-next-api/gitpod/v1/organization_pb";
import { type FC, useCallback, useEffect, useState } from "react";

export const MAX_MESSAGE_LENGTH = 1000;

export const AnnouncementBannerPage: FC = () => {
    useDocumentTitle("Announcements");

    const { membership, isPending: isLoadingMembership } = useMembership();
    const { data: organization, isPending: isLoadingOrganization } = useOrganization();
    const { data: banner, isPending: isLoadingBanner } = useGetAnnouncementBanner();
    const updateBanner = useUpdateAnnouncementBanner();
    const { toast } = useToast();

    const [message, setMessage] = useState("");
    const [enabled, setEnabled] = useState(false);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

    const isMutating = updateBanner.isPending;

    useEffect(() => {
        if (banner) {
            setMessage(banner.message);
            setEnabled(banner.enabled);
            setHasUnsavedChanges(false);
        }
    }, [banner]);

    const handleMessageChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const newMessage = e.target.value;
        if (newMessage.length <= MAX_MESSAGE_LENGTH) {
            setMessage(newMessage);
            setHasUnsavedChanges(true);
        }
    }, []);

    const handleSave = useCallback(async () => {
        try {
            await updateBanner.mutateAsync({ message, enabled });
            setHasUnsavedChanges(false);
            toast({
                title: "Announcement banner saved",
            });
        } catch (error) {
            toast({
                title: "Failed to save announcement banner",
                description: formatError(error),
            });
        }
    }, [message, enabled, updateBanner, toast]);

    const handleToggle = useCallback(
        async (newEnabled: boolean) => {
            const serverState = banner?.enabled ?? false;
            setEnabled(newEnabled);

            try {
                await updateBanner.mutateAsync({ message, enabled: newEnabled });
                setHasUnsavedChanges(false);
                toast({
                    title: newEnabled ? "Announcement banner enabled" : "Announcement banner disabled",
                });
            } catch (error) {
                setEnabled(serverState);
                toast({
                    title: "Failed to update announcement banner",
                    description: formatError(error),
                });
            }
        },
        [banner?.enabled, message, updateBanner, toast],
    );

    if (isLoadingMembership || !membership || isLoadingOrganization || !organization || isLoadingBanner) {
        return <LoadingState />;
    }

    if (membership.userRole !== OrganizationRole.ADMIN) {
        return <NotAuthorized />;
    }

    const isEnterpriseTier = organization.tier === OrganizationTier.ENTERPRISE;

    if (!isEnterpriseTier) {
        return (
            <div className="flex max-w-[46rem] flex-col gap-4" data-testid="announcement-banner-page">
                <Text className="text-base text-content-secondary">
                    Configure an announcement banner that displays to all organization members.
                </Text>
                <Banner
                    variant="info"
                    className="py-4"
                    data-testid="enterprise-tier-banner"
                    text={
                        <>
                            Upgrade to{" "}
                            <Link
                                to="/settings/manage-organization"
                                className="font-medium text-content-brand hover:underline"
                            >
                                Enterprise tier
                            </Link>{" "}
                            to configure announcement banners.
                        </>
                    }
                />
            </div>
        );
    }

    const charactersRemaining = MAX_MESSAGE_LENGTH - message.length;

    return (
        <div className="flex max-w-[46rem] flex-col gap-6" data-testid="announcement-banner-page">
            <Text className="text-base text-content-secondary">
                Configure an announcement banner that displays to all organization members.
            </Text>

            <div className="flex flex-col gap-4 rounded-xl border border-border-base p-5">
                <div className="flex items-center gap-4">
                    <Switch
                        id="announcement-banner-enabled"
                        data-testid="announcement-banner-enabled"
                        state={isMutating ? "pending" : enabled ? "checked" : "unchecked"}
                        onToggle={handleToggle}
                        disabled={isMutating || (!enabled && !message.trim())}
                        aria-label="Enable announcement banner"
                    />
                    <div className="flex flex-col gap-1">
                        <label htmlFor="announcement-banner-enabled" className="cursor-pointer font-bold">
                            Enable announcement banner
                        </label>
                        <Text className="text-content-secondary">
                            When enabled, the banner will be visible to all organization members.
                        </Text>
                    </div>
                </div>

                <div className="flex flex-col gap-2">
                    <label htmlFor="announcement-message" className="text-sm font-medium text-content-primary">
                        Banner message
                    </label>
                    <Textarea
                        id="announcement-message"
                        data-testid="announcement-message-input"
                        value={message}
                        onChange={handleMessageChange}
                        placeholder="Enter your announcement message. Supports Markdown formatting."
                        className="min-h-[100px]"
                        maxLength={MAX_MESSAGE_LENGTH}
                        disabled={isMutating}
                    />
                    <div className="flex justify-between text-sm">
                        <Text className="text-content-secondary">Supports Markdown: **bold**, *italic*, [links](url)</Text>
                        <Text
                            data-testid="character-counter"
                            className={charactersRemaining < 100 ? "text-content-warning" : "text-content-secondary"}
                        >
                            {charactersRemaining} characters remaining
                        </Text>
                    </div>
                </div>

                {message && (
                    <div className="flex flex-col gap-2">
                        <Text className="text-sm font-medium text-content-primary">Preview</Text>
                        <Banner
                            variant="announcement"
                            data-testid="announcement-banner-preview"
                            text={<AnnouncementMarkdown content={message} className="text-inherit" />}
                        />
                    </div>
                )}

                <div className="flex justify-end">
                    <Button
                        data-testid="save-announcement-button"
                        data-tracking-id="save-announcement-banner"
                        variant="primary"
                        onClick={handleSave}
                        disabled={!hasUnsavedChanges || isMutating || !message.trim()}
                        loading={isMutating}
                    >
                        Save
                    </Button>
                </div>
            </div>
        </div>
    );
};
